<template>
  <div class="container-fluid py-4 user-dashboard" v-if="user">
    <Loading v-if="loading" />

    <div class="ud-top mb-4">
      <div>
        <h4 class="mb-1 text-dark font-weight-bolder">Dashboard</h4>
        <p class="text-sm text-secondary mb-0">
          Última atualização:
          {{ lastUpdatedLabel }}
        </p>
      </div>
      <div class="ud-top-actions">
        <button
          type="button"
          class="btn btn-outline-secondary mb-0"
          @click="refreshAll"
          :disabled="loading"
        >
          <i class="material-icons-round text-sm align-middle">refresh</i>
          Atualizar
        </button>
        <router-link
          class="btn bg-gradient-success text-white mb-0"
          :to="{ name: RouteName.NewRaffle }"
        >
          Nova campanha
        </router-link>
      </div>
    </div>

    <div
      v-if="showOnboardingChecklist"
      class="card mb-4 ud-onboarding-card"
    >
      <div class="card-body p-3">
        <div class="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
          <div>
            <p class="text-xs text-uppercase text-secondary font-weight-bolder mb-1">
              Primeiros passos
            </p>
            <h6 class="mb-1 font-weight-bolder">Monte sua primeira campanha</h6>
            <p class="text-sm text-secondary mb-0">
              Siga os passos abaixo — tudo dentro do Rifa365.
            </p>
          </div>
          <button
            type="button"
            class="btn btn-sm btn-link text-secondary mb-0"
            @click="dismissOnboarding"
          >
            Dispensar
          </button>
        </div>
        <div class="ud-onboarding-steps">
          <div
            v-for="(step, idx) in onboardingSteps"
            :key="step.key"
            class="ud-onboarding-step"
            :class="{ 'is-done': step.done }"
          >
            <div class="ud-onboarding-index">
              <i v-if="step.done" class="material-icons-round">check</i>
              <span v-else>{{ idx + 1 }}</span>
            </div>
            <div class="ud-onboarding-body min-width-0">
              <strong class="d-block text-sm">{{ step.title }}</strong>
              <span class="text-xs text-secondary">{{ step.hint }}</span>
            </div>
            <router-link
              v-if="!step.done"
              class="btn btn-sm bg-gradient-primary text-white mb-0"
              :to="step.to"
            >
              {{ step.cta }}
            </router-link>
            <span v-else class="badge bg-gradient-success mb-0">Feito</span>
          </div>
        </div>
      </div>
    </div>

    <div class="card mb-4">
      <div class="card-body p-3">
        <div class="d-flex flex-wrap gap-2 align-items-center">
          <span class="text-sm font-weight-bold me-2">Período:</span>
          <button
            v-for="opt in periodOptions"
            :key="opt.key"
            type="button"
            class="btn btn-sm mb-0"
            :class="
              filterType === opt.key
                ? 'bg-gradient-primary'
                : 'btn-outline-primary'
            "
            @click="setFilter(opt.key)"
          >
            {{ opt.label }}
          </button>
          <template v-if="filterType === 'custom'">
            <input
              type="date"
              class="form-control form-control-sm ud-date"
              v-model="customStart"
            />
            <span class="text-sm text-secondary">até</span>
            <input
              type="date"
              class="form-control form-control-sm ud-date"
              v-model="customEnd"
            />
            <button
              type="button"
              class="btn btn-sm bg-gradient-success text-white mb-0"
              @click="loadMetrics"
            >
              Aplicar
            </button>
          </template>
        </div>
      </div>
    </div>

    <div class="card mb-4 ud-premia-card" v-if="premiacoesProgress">
      <div class="card-body p-3">
        <div class="d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div class="d-flex align-items-center gap-3 min-width-0">
            <img
              v-if="premiacoesProgress.current"
              :src="premiacoesProgress.current.image"
              :alt="premiacoesProgress.current.name"
              class="ud-premia-thumb"
            />
            <div
              v-else
              class="ud-premia-thumb ud-premia-thumb--empty d-flex align-items-center justify-content-center"
            >
              <i class="material-icons-round">emoji_events</i>
            </div>
            <div class="min-width-0">
              <p class="ud-premia-eyebrow mb-1">
                Premiações Rifa365
              </p>
              <h6 class="mb-1 ud-premia-title">
                <template v-if="premiacoesProgress.current">
                  Nível {{ premiacoesProgress.current.name }}
                </template>
                <template v-else>Em busca do Ticket Start</template>
              </h6>
              <p class="text-sm ud-premia-meta mb-0">
                Faturamento:
                {{ formatMoney(premiacoesProgress.revenue) }}
                <template v-if="premiacoesProgress.next">
                  · faltam
                  {{ formatMoney(premiacoesProgress.remaining) }} para
                  {{ premiacoesProgress.next.name }}
                </template>
                <template v-else> · Hall of Fame conquistado</template>
              </p>
            </div>
          </div>
          <div class="ud-premia-right flex-grow-1" style="max-width: 320px">
            <div class="progress ud-premia-bar mb-2">
              <div
                class="progress-bar ud-premia-bar-fill"
                :style="{ width: premiacoesProgress.progressPct + '%' }"
              ></div>
            </div>
            <div class="d-flex flex-wrap gap-2">
              <button
                v-if="premiacoesProgress.current"
                type="button"
                class="btn btn-sm ud-premia-cta mb-0"
                @click="solicitarPlacaAtual"
              >
                Solicitar placa
              </button>
              <router-link
                class="btn btn-sm ud-premia-cta-outline mb-0"
                to="/premiacoes"
              >
                Ver trilha de prêmios
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-xl-3 col-sm-6 mb-4" v-for="card in kpiCards" :key="card.key">
        <div class="card ud-kpi h-100">
          <div class="card-body p-3">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <p class="text-xs text-uppercase text-secondary font-weight-bolder mb-1">
                  {{ card.label }}
                </p>
                <h5 class="mb-0 font-weight-bolder text-dark">
                  <template v-if="card.key === 'revenue' && hideRevenue">
                    ••••••
                  </template>
                  <template v-else>{{ card.value }}</template>
                </h5>
                <p v-if="card.hint" class="text-xs text-secondary mb-0 mt-1">
                  {{ card.hint }}
                </p>
              </div>
              <div class="ud-kpi-icon" :class="card.tone">
                <i class="material-icons-round">{{ card.icon }}</i>
              </div>
            </div>
            <button
              v-if="card.key === 'revenue'"
              type="button"
              class="btn btn-link btn-sm p-0 mt-2 text-secondary"
              @click="hideRevenue = !hideRevenue"
            >
              <i class="material-icons-round text-sm align-middle">{{
                hideRevenue ? "visibility" : "visibility_off"
              }}</i>
              {{ hideRevenue ? "Mostrar" : "Ocultar" }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="row mb-4">
      <div class="col-lg-7 mb-4 mb-lg-0">
        <div class="card h-100">
          <div class="card-header pb-0">
            <h6 class="mb-0">Faturamento no período</h6>
            <p class="text-sm text-secondary mb-0">
              Vendas aprovadas das suas campanhas
            </p>
          </div>
          <div class="card-body">
            <DashboardChart
              v-if="chartData"
              :data="chartData"
              type="line"
              :options="chartOptions"
            />
            <p v-else class="text-sm text-secondary mb-0 text-center py-5">
              Sem vendas aprovadas neste período.
            </p>
          </div>
        </div>
      </div>
      <div class="col-lg-5">
        <div class="card h-100">
          <div class="card-header pb-0">
            <h6 class="mb-0">Campanhas em destaque</h6>
          </div>
          <div class="card-body">
            <div v-if="!topCampaigns.length" class="text-sm text-secondary">
              Nenhuma campanha ainda.
              <router-link :to="{ name: RouteName.NewRaffle }"
                >Criar agora</router-link
              >
            </div>
            <div
              v-for="camp in topCampaigns"
              :key="camp.id"
              class="ud-campaign mb-3"
            >
              <div class="d-flex justify-content-between gap-2">
                <div class="min-width-0">
                  <p class="mb-0 text-sm font-weight-bold text-dark text-truncate">
                    {{ camp.name }}
                  </p>
                  <p class="mb-1 text-xs text-secondary">
                    {{ camp.status }} ·
                    {{ camp.quotas_sold || 0 }}/{{ camp.qnt_quotas || 0 }} cotas
                  </p>
                </div>
                <strong class="text-sm text-success text-nowrap">
                  {{
                    hideRevenue
                      ? "••••"
                      : formatMoney(camp.period_revenue || camp.total_collected || 0)
                  }}
                </strong>
              </div>
              <div class="progress ud-progress">
                <div
                  class="progress-bar bg-gradient-primary"
                  :style="{ width: progressPct(camp) + '%' }"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row mb-4">
      <div class="col-12">
        <div class="card">
          <div class="card-body p-3 d-flex flex-wrap gap-2">
            <router-link class="btn btn-outline-dark mb-0" to="/premiacoes">
              Premiações
            </router-link>
            <router-link class="btn btn-outline-dark mb-0" to="/sorteador">
              Sorteador
            </router-link>
            <router-link class="btn btn-outline-dark mb-0" to="/ranking">
              Ranking
            </router-link>
            <router-link class="btn btn-outline-dark mb-0" to="/orders">
              Pedidos
            </router-link>
            <router-link
              class="btn btn-outline-dark mb-0"
              :to="{ name: 'Configurações', query: { tab: 'payment_methods' } }"
            >
              Método de pagamento
            </router-link>
            <router-link
              v-if="hasSystemWallet"
              class="btn btn-outline-dark mb-0"
              to="/wallet"
            >
              Carteira
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-lg-6 mb-4">
        <div class="card h-100 ud-tool-card">
          <div class="card-header pb-0">
            <h6 class="mb-0">Buscar ganhador</h6>
            <p class="text-sm text-secondary mb-0">
              Informe a campanha e o número da cota
            </p>
          </div>
          <div class="card-body">
            <div class="ud-field mb-3">
              <label class="ud-field-label">Campanha</label>
              <select class="ud-field-control" v-model="winnerCampaignId">
                <option value="">Selecione...</option>
                <option
                  v-for="c in campaignOptions"
                  :key="c.id"
                  :value="c.id"
                >
                  {{ c.name }}
                </option>
              </select>
            </div>
            <div class="ud-field mb-3">
              <label class="ud-field-label">Número da cota</label>
              <input
                class="ud-field-control"
                type="number"
                min="0"
                v-model="winnerQuota"
                placeholder="Ex: 562"
              />
            </div>
            <button
              type="button"
              class="btn bg-gradient-primary text-white mb-0"
              :disabled="!winnerCampaignId || winnerQuota === '' || searchingWinner"
              @click="searchWinner"
            >
              {{ searchingWinner ? "Buscando..." : "Buscar" }}
            </button>

            <div v-if="winnerResult" class="ud-winner mt-3">
              <template v-if="winnerResult.found">
                <p class="mb-1 text-sm">
                  <strong>Nome:</strong> {{ winnerResult.name }}
                </p>
                <p class="mb-1 text-sm">
                  <strong>WhatsApp:</strong>
                  {{ formatPhonePrivacy(winnerResult.phone) }}
                </p>
                <p class="mb-2 text-sm">
                  <strong>Pedido:</strong> #{{ winnerResult.id }}
                </p>
                <div class="ud-winner-actions d-flex flex-wrap gap-2">
                  <a
                    v-if="winnerResult.phone"
                    class="btn btn-sm bg-gradient-success text-white mb-0"
                    :href="whatsAppUrl(winnerResult.phone)"
                    target="_blank"
                    rel="noopener"
                  >
                    Falar no WhatsApp
                  </a>
                  <button
                    v-if="canFinalizeWinner"
                    type="button"
                    class="btn btn-sm bg-gradient-primary text-white mb-0"
                    :disabled="finalizingWinner || isLoadingWinnerPrizes"
                    @click="finalizeWinnerFromDashboard"
                  >
                    {{
                      finalizingWinner || isLoadingWinnerPrizes
                        ? "Carregando..."
                        : "Finalizar campanha"
                    }}
                  </button>
                </div>
                <p
                  v-if="showWinnerFinishedHint"
                  class="text-xs text-secondary mb-0 mt-2"
                >
                  Esta campanha já está finalizada.
                </p>
              </template>
              <p v-else class="text-sm text-secondary mb-0">
                Nenhuma compra encontrada para essa cota.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-6 mb-4">
        <div class="card h-100 ud-tool-card">
          <div class="card-header pb-0">
            <h6 class="mb-0">Ranking de compradores</h6>
            <p class="text-sm text-secondary mb-0">Top do período selecionado</p>
          </div>
          <div class="card-body">
            <div class="ud-field mb-3">
              <label class="ud-field-label">Campanha</label>
              <select
                class="ud-field-control"
                v-model="rankingCampaignId"
                @change="loadRanking"
              >
                <option value="">Selecione...</option>
                <option
                  v-for="c in campaignOptions"
                  :key="c.id"
                  :value="c.id"
                >
                  {{ c.name }}
                </option>
              </select>
            </div>

            <div v-if="loadingRanking" class="text-sm text-secondary">
              Carregando ranking...
            </div>
            <div v-else-if="!rankingRows.length" class="ud-empty-hint text-sm">
              Selecione uma campanha para ver o ranking.
            </div>
            <ul v-else class="list-group list-group-flush ud-ranking">
              <li
                v-for="(row, idx) in rankingRows"
                :key="row.phone || idx"
                class="list-group-item px-0 d-flex justify-content-between align-items-center"
              >
                <div>
                  <span class="badge bg-gradient-primary me-2">{{ idx + 1 }}</span>
                  <strong class="text-sm">{{ row.name }}</strong>
                  <div class="text-xs text-secondary">
                    {{ formatPhonePrivacy(row.phone) }}
                  </div>
                </div>
                <div class="text-end">
                  <div class="text-sm font-weight-bold">
                    {{ row.total_quotas || row.qnt_quotas || 0 }} cotas
                  </div>
                  <div class="text-xs text-success">
                    {{ formatMoney(row.total_value || 0) }}
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <SetWinnersDialog
      :isOpen="winnersDialogOpen"
      :isLoading="isLoadingWinnerPrizes"
      :prizes="winnerPrizes"
      :campaignId="Number(winnerCampaignId) || 0"
      :isDetails="false"
      @close="closeWinnersDialog"
      @endCampaign="onWinnerCampaignEnded"
    />

    <ConfirmDialog
      :isOpen="isWinnerConfirmOpen"
      title="Encerrar campanha"
      :removeText="winnerConfirmText"
      @close="isWinnerConfirmOpen = false"
      @confirm="confirmSinglePrizeFinalize"
    />
  </div>
</template>

<script>
import Loading from "@/components/Loading.vue";
import DashboardChart from "./DashboardChart.vue";
import SetWinnersDialog from "@/views/Raffles/RafflesList/components/SetWinnersDialog.vue";
import ConfirmDialog from "@/views/components/ConfirmDialog.vue";
import DashboardHttp from "@/http/DashboardHttp.js";
import RafflesHttp from "@/http/RafflesHttp.js";
import WalletHttp from "@/http/WalletHttp.js";
import WinnersHttp from "@/http/WinnersHttp.js";
import ParticipantsHttp from "@/http/ParticipantsHttp.js";
import { RouteName } from "@/store/routeName";
import { getPremiacoesProgress, buildSolicitarPlacaWhatsAppUrl } from "@/constants/premiacoesTiers.js";
import { toWhatsAppUrl } from "@/helpers/phoneNumber.js";

export default {
  name: "UserDashboard",
  components: {
    Loading,
    DashboardChart,
    SetWinnersDialog,
    ConfirmDialog,
  },
  data() {
    return {
      user: null,
      loading: false,
      hideRevenue: false,
      lastUpdated: null,
      filterType: "month",
      customStart: "",
      customEnd: "",
      dateStart: "",
      dateEnd: "",
      metrics: null,
      walletBalance: 0,
      campaignOptions: [],
      topCampaigns: [],
      winnerCampaignId: "",
      winnerQuota: "",
      searchingWinner: false,
      winnerResult: null,
      winnersDialogOpen: false,
      isLoadingWinnerPrizes: false,
      winnerPrizes: null,
      isWinnerConfirmOpen: false,
      finalizingWinner: false,
      winnerConfirmText:
        "Ao informar o ganhador, a campanha será automaticamente marcada como concluída. Após a conclusão, não será possível realizar novas ações ou alterar qualquer informação relacionada à campanha. Tem certeza que deseja continuar?",
      rankingCampaignId: "",
      rankingRows: [],
      loadingRanking: false,
      onboardingDismissed: false,
      periodOptions: [
        { key: "today", label: "Hoje" },
        { key: "week", label: "7 dias" },
        { key: "month", label: "Mês" },
        { key: "custom", label: "Personalizado" },
      ],
    };
  },
  computed: {
    RouteName() {
      return RouteName;
    },
    lastUpdatedLabel() {
      if (!this.lastUpdated) return "—";
      return this.lastUpdated.toLocaleString("pt-BR");
    },
    hasSystemWallet() {
      return !!this.user?.system_wallet;
    },
    hasPaymentMethod() {
      return (
        this.hasSystemWallet ||
        Number(this.user?.default_provider || 0) > 0
      );
    },
    campaignsTotal() {
      return Number(this.metrics?.campaigns_total || 0);
    },
    selectedWinnerCampaign() {
      return this.campaignOptions.find(
        (campaign) => String(campaign.id) === String(this.winnerCampaignId)
      );
    },
    canFinalizeWinner() {
      return (
        this.winnerResult?.found &&
        this.selectedWinnerCampaign?.status === "ACTIVE"
      );
    },
    showWinnerFinishedHint() {
      return (
        this.winnerResult?.found &&
        this.selectedWinnerCampaign?.status === "FINISHED"
      );
    },
    showOnboardingChecklist() {
      if (this.onboardingDismissed) return false;
      if (this.metrics == null) return false;
      return this.campaignsTotal < 1;
    },
    onboardingSteps() {
      return [
        {
          key: "pix",
          title: "Configurar recebimento Pix",
          hint: this.hasSystemWallet
            ? "Carteira do sistema já ativa."
            : "Cadastre sua chave ou ative a carteira.",
          cta: "Configurar",
          done: this.hasPaymentMethod,
          to: { name: "Configurações", query: { tab: "payment_methods" } },
        },
        {
          key: "create",
          title: "Criar sua primeira campanha",
          hint: "Defina prêmio, cotas e valor.",
          cta: "Criar",
          done: this.campaignsTotal >= 1,
          to: { name: RouteName.NewRaffle },
        },
        {
          key: "learn",
          title: "Ver um tutorial rápido",
          hint: "Aprenda sem sair do sistema.",
          cta: "Tutoriais",
          done: false,
          to: { name: "Tutoriais" },
        },
      ];
    },
    premiacoesProgress() {
      return getPremiacoesProgress(this.metrics?.lifetime_revenue || 0);
    },
    kpiCards() {
      const m = this.metrics || {};
      const cards = [
        {
          key: "campaigns",
          label: "Campanhas",
          value: String(m.campaigns_total || 0),
          hint: `${m.campaigns_active || 0} ativas`,
          icon: "campaign",
          tone: "tone-purple",
        },
        {
          key: "quotas",
          label: "Cotas vendidas",
          value: this.formatInt(m.quotas_sold_period || 0),
          hint: `Total: ${this.formatInt(m.quotas_sold_lifetime || 0)}`,
          icon: "confirmation_number",
          tone: "tone-blue",
        },
        {
          key: "revenue",
          label: "Faturamento",
          value: this.formatMoney(m.revenue || 0),
          hint: `${m.orders_approved || 0} pedidos · ${m.unique_buyers || 0} compradores`,
          icon: "payments",
          tone: "tone-pink",
        },
      ];

      if (this.hasSystemWallet) {
        cards.push({
          key: "wallet",
          label: "Carteira",
          value: this.formatMoney(this.walletBalance || 0),
          hint: "Saldo disponível",
          icon: "account_balance_wallet",
          tone: "tone-green",
        });
      }

      return cards;
    },
    chartData() {
      const series = this.metrics?.revenue_series || [];
      if (!series.length) return null;
      return {
        labels: series.map((s) => this.formatChartDate(s.date)),
        datasets: [
          {
            label: "Faturamento",
            data: series.map((s) => Number(s.total) || 0),
            borderColor: "#e91e63",
            backgroundColor: "rgba(233, 30, 99, 0.15)",
            fill: true,
            tension: 0.35,
          },
        ],
      };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
        },
        scales: {
          y: {
            ticks: {
              callback: (v) =>
                new Intl.NumberFormat("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                  maximumFractionDigits: 0,
                }).format(v),
            },
          },
        },
      };
    },
  },
  async mounted() {
    this.user = this.$store.getters.user;
    this.onboardingDismissed = this.readOnboardingDismissed();
    this.applyDateRange(this.filterType);
    await this.refreshAll();
  },
  methods: {
    solicitarPlacaAtual() {
      const tier = this.premiacoesProgress?.current;
      if (!tier) return;
      const url = buildSolicitarPlacaWhatsAppUrl(
        this.user?.full_name || this.user?.name,
        tier
      );
      if (url) window.open(url, "_blank");
    },
    onboardingStorageKey() {
      const id = this.user?.id || "anon";
      return `rifa365_onboarding_dismissed_${id}`;
    },
    readOnboardingDismissed() {
      try {
        return localStorage.getItem(this.onboardingStorageKey()) === "1";
      } catch {
        return false;
      }
    },
    dismissOnboarding() {
      this.onboardingDismissed = true;
      try {
        localStorage.setItem(this.onboardingStorageKey(), "1");
      } catch {
        /* ignore */
      }
    },
    formatMoney(value) {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(Number(value) || 0);
    },
    formatInt(value) {
      return Number(value || 0).toLocaleString("pt-BR");
    },
    formatChartDate(value) {
      if (!value) return "";
      const [, m, d] = String(value).split("-");
      return `${d}/${m}`;
    },
    formatPhonePrivacy(phone) {
      const digits = String(phone || "").replace(/\D/g, "");
      if (!digits) return "—";
      const last4 = digits.slice(-4);
      if (digits.length >= 11) {
        return `(${digits.slice(0, 2)}) ${digits.slice(2, 3)} ****-${last4}`;
      }
      if (digits.length >= 10) {
        return `(${digits.slice(0, 2)}) ****-${last4}`;
      }
      return `****-${last4}`;
    },
    whatsAppUrl(phone) {
      return toWhatsAppUrl(phone) || "#";
    },
    progressPct(camp) {
      const total = Number(camp.qnt_quotas) || 0;
      const sold = Number(camp.quotas_sold) || 0;
      if (!total) return 0;
      return Math.min(100, Math.round((sold / total) * 100));
    },
    setFilter(type) {
      this.filterType = type;
      if (type === "custom") return;
      this.applyDateRange(type);
      this.loadMetrics();
      if (this.rankingCampaignId) this.loadRanking();
    },
    applyDateRange(type) {
      const now = new Date();
      const toYmd = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
      };
      this.dateEnd = toYmd(now);
      if (type === "today") {
        this.dateStart = toYmd(now);
      } else if (type === "week") {
        const start = new Date(now);
        start.setDate(start.getDate() - 6);
        this.dateStart = toYmd(start);
      } else if (type === "month") {
        this.dateStart = `${now.getFullYear()}-${String(
          now.getMonth() + 1
        ).padStart(2, "0")}-01`;
      } else if (type === "custom") {
        this.dateStart = this.customStart || this.dateStart;
        this.dateEnd = this.customEnd || this.dateEnd;
      }
      this.customStart = this.dateStart;
      this.customEnd = this.dateEnd;
    },
    async refreshAll() {
      this.loading = true;
      try {
        const tasks = [this.loadMetrics(), this.loadCampaignOptions()];
        if (this.hasSystemWallet) {
          tasks.push(this.loadWallet());
        } else {
          this.walletBalance = 0;
        }
        // Não trava o painel se um endpoint demorar/falhar.
        await Promise.allSettled(
          tasks.map((p) =>
            Promise.race([
              p,
              new Promise((_, reject) =>
                setTimeout(() => reject(new Error("dashboard_timeout")), 20000)
              ),
            ])
          )
        );
        this.lastUpdated = new Date();
      } finally {
        this.loading = false;
      }
    },
    async loadMetrics() {
      if (this.filterType === "custom") {
        this.dateStart = this.customStart;
        this.dateEnd = this.customEnd;
      }
      try {
        const res = await DashboardHttp.getUserMetrics({
          date_start: this.dateStart,
          date_end: this.dateEnd,
        });
        this.metrics = res.data || {};
        this.topCampaigns = Array.isArray(this.metrics.top_campaigns)
          ? this.metrics.top_campaigns
          : [];
        this.lastUpdated = new Date();
      } catch (err) {
        console.error(err);
        await this.loadMetricsFallback();
      }
    },
    async loadMetricsFallback() {
      try {
        const all = [];
        let page = 1;
        let pages = 1;
        do {
          const res = await RafflesHttp.getCampaigns("", page, 100);
          const campaigns = res?.data?.campaigns || [];
          pages = Number(res?.data?.pages) || 1;
          all.push(...campaigns);
          page += 1;
        } while (page <= pages);

        const campaignsActive = all.filter((c) => c.status === "ACTIVE").length;
        const quotasSold = all.reduce(
          (acc, c) =>
            acc +
            Math.max(
              0,
              (Number(c.qnt_quotas) || 0) - (Number(c.remaining_quotas) || 0)
            ),
          0
        );
        const quotasTotal = all.reduce(
          (acc, c) => acc + (Number(c.qnt_quotas) || 0),
          0
        );
        const lifetimeRevenue = all.reduce(
          (acc, c) => acc + (Number(c.total_collected) || 0),
          0
        );
        this.metrics = {
          campaigns_total: all.length,
          campaigns_active: campaignsActive,
          revenue: lifetimeRevenue,
          lifetime_revenue: lifetimeRevenue,
          orders_approved: 0,
          unique_buyers: 0,
          quotas_sold_period: quotasSold,
          quotas_sold_lifetime: quotasSold,
          quotas_total: quotasTotal,
          revenue_series: [],
          top_campaigns: [...all]
            .sort(
              (a, b) =>
                (Number(b.total_collected) || 0) -
                (Number(a.total_collected) || 0)
            )
            .slice(0, 5)
            .map((c) => ({
              ...c,
              quotas_sold:
                (Number(c.qnt_quotas) || 0) - (Number(c.remaining_quotas) || 0),
              period_revenue: Number(c.total_collected) || 0,
            })),
        };
        this.topCampaigns = this.metrics.top_campaigns;
      } catch (err) {
        console.error(err);
        this.metrics = {};
        this.topCampaigns = [];
      }
    },
    async loadWallet() {
      try {
        const res = await WalletHttp.getWalletBalance();
        this.walletBalance =
          res?.data?.balance ?? res?.data?.wallet?.balance ?? 0;
      } catch (_) {
        this.walletBalance = 0;
      }
    },
    async loadCampaignOptions() {
      try {
        const res = await RafflesHttp.getRafflesMinimum();
        this.campaignOptions = Array.isArray(res.data) ? res.data : [];
      } catch (_) {
        this.campaignOptions = [];
      }
    },
    async searchWinner() {
      if (!this.winnerCampaignId || this.winnerQuota === "") return;
      this.searchingWinner = true;
      this.winnerResult = null;
      try {
        const res = await WinnersHttp.getParticipant(
          this.winnerCampaignId,
          this.winnerQuota
        );
        const data = res?.data?.data || res?.data;
        if (data?.id) {
          this.winnerResult = {
            found: true,
            id: data.id,
            name: data.name,
            phone: data.phone,
          };
        } else {
          this.winnerResult = { found: false };
        }
      } catch (_) {
        this.winnerResult = { found: false };
      } finally {
        this.searchingWinner = false;
      }
    },
    async finalizeWinnerFromDashboard() {
      if (!this.canFinalizeWinner || !this.winnerCampaignId) return;

      this.isLoadingWinnerPrizes = true;
      try {
        const response = await WinnersHttp.getAwards(this.winnerCampaignId);
        const awards = Array.isArray(response.data) ? response.data : [];

        if (!awards.length) {
          this.notifyWinnerAction(
            "warn",
            "Sem prêmios",
            "Esta campanha não possui prêmios cadastrados."
          );
          return;
        }

        this.winnerPrizes = awards;

        if (awards.length === 1) {
          this.isWinnerConfirmOpen = true;
          return;
        }

        this.winnerPrizes = awards.map((prize, index) => ({
          ...prize,
          quota: index === 0 ? Number(this.winnerQuota) : prize.quota,
        }));
        this.winnersDialogOpen = true;
      } catch (error) {
        console.error(error);
        this.notifyWinnerAction(
          "error",
          "Erro",
          "Não foi possível carregar os prêmios da campanha."
        );
      } finally {
        this.isLoadingWinnerPrizes = false;
      }
    },
    async confirmSinglePrizeFinalize() {
      const prize = this.winnerPrizes?.[0];
      if (!prize?.id || this.winnerQuota === "") return;

      this.finalizingWinner = true;
      try {
        await WinnersHttp.setCampaignWinners(this.winnerCampaignId, [
          {
            id: prize.id,
            quota: Number(this.winnerQuota),
          },
        ]);
        this.notifyWinnerAction(
          "success",
          "Campanha finalizada",
          "O ganhador foi registrado e a campanha foi encerrada."
        );
        await this.onWinnerCampaignEnded();
      } catch (error) {
        console.error(error);
        this.notifyWinnerAction(
          "error",
          "Erro",
          error?.response?.data?.error ||
            error?.error ||
            "Não foi possível finalizar a campanha."
        );
      } finally {
        this.finalizingWinner = false;
        this.isWinnerConfirmOpen = false;
      }
    },
    closeWinnersDialog() {
      this.winnersDialogOpen = false;
      this.winnerPrizes = null;
    },
    async onWinnerCampaignEnded() {
      this.closeWinnersDialog();
      this.isWinnerConfirmOpen = false;
      await this.loadCampaignOptions();
      await this.refreshAll();
    },
    notifyWinnerAction(severity, summary, detail) {
      if (this.$toast?.add) {
        this.$toast.add({ severity, summary, detail, life: 4000 });
      }
    },
    async loadRanking() {
      this.rankingRows = [];
      if (!this.rankingCampaignId) return;
      this.loadingRanking = true;
      try {
        const res = await ParticipantsHttp.getRanking(
          this.rankingCampaignId,
          1,
          {
            initial: `${this.dateStart} 00:00:00`,
            final: `${this.dateEnd} 23:59:59`,
          }
        );
        const data = res?.data?.data || res?.data || [];
        this.rankingRows = Array.isArray(data) ? data.slice(0, 8) : [];
      } catch (err) {
        console.error(err);
        this.rankingRows = [];
      } finally {
        this.loadingRanking = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.user-dashboard {
  max-width: 1400px;
}

.ud-top {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  flex-wrap: wrap;
}

.ud-top-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.ud-date {
  width: 150px;
}

.ud-kpi {
  border: 1px solid #edf0f5;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
}

.ud-kpi-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}

.tone-purple {
  background: linear-gradient(135deg, #7c3aed, #a855f7);
}
.tone-blue {
  background: linear-gradient(135deg, #2563eb, #38bdf8);
}
.tone-pink {
  background: linear-gradient(135deg, #e11d48, #db2777);
}
.tone-green {
  background: linear-gradient(135deg, #059669, #34d399);
}

.ud-campaign {
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f1f5f9;
}

.ud-campaign:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.ud-progress {
  height: 6px;
  border-radius: 999px;
  background: #eef2ff;
}

.ud-winner {
  padding: 0.85rem;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.ud-winner-actions {
  margin-top: 0.15rem;
}

.ud-tool-card {
  border: 1px solid #edf0f5;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
}

.ud-field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.ud-field-label {
  margin: 0;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #344767;
}

.ud-field-control {
  width: 100%;
  min-height: 44px;
  border: 1px solid #d2d6da;
  border-radius: 10px;
  background: #fff;
  color: #344767;
  font-size: 0.9rem;
  line-height: 1.4;
  padding: 0.65rem 0.85rem;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  appearance: none;
  -webkit-appearance: none;
}

select.ud-field-control {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%237b809a' d='M1.4 0.6 6 5.2 10.6.6 12 2 6 8 0 2z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.9rem center;
  padding-right: 2.2rem;
  cursor: pointer;
}

.ud-field-control:hover {
  border-color: #b0b7c3;
}

.ud-field-control:focus {
  border-color: #7b23d3;
  box-shadow: 0 0 0 3px rgba(123, 35, 211, 0.15);
}

.ud-field-control::placeholder {
  color: #98a2b3;
}

.ud-empty-hint {
  margin: 0;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  background: #f8f9fb;
  border: 1px dashed #d8dee9;
  color: #67748e;
}

.ud-onboarding-card {
  border: 1px solid rgba(94, 41, 176, 0.18);
  border-radius: 14px;
  background: #faf8ff;
}

.ud-onboarding-steps {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ud-onboarding-step {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #ece8f5;
}

.ud-onboarding-step.is-done {
  border-color: rgba(46, 125, 50, 0.25);
  background: #f7fbf7;
}

.ud-onboarding-index {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #5e29b0;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
}

.ud-onboarding-step.is-done .ud-onboarding-index {
  background: #2e7d32;
}

.ud-onboarding-index .material-icons-round {
  font-size: 16px;
}

.ud-onboarding-body {
  flex: 1;
}

.ud-premia-card {
  border: 1px solid rgba(184, 144, 60, 0.28);
  background: linear-gradient(120deg, #fffdf8 0%, #f4ead6 48%, #fffaf2 100%);
  border-radius: 16px;
  box-shadow: 0 12px 28px rgba(60, 42, 18, 0.08);
}

.ud-premia-eyebrow {
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 800;
  color: #9a7428;
  margin: 0;
}

.ud-premia-title {
  color: #1a1714;
  font-weight: 800;
}

.ud-premia-meta {
  color: #6f675e;
}

.ud-premia-thumb {
  width: 64px;
  height: 64px;
  object-fit: contain;
  border-radius: 12px;
  flex-shrink: 0;
  background: linear-gradient(160deg, #1f1b17 0%, #2c241c 100%);
  border: 1px solid rgba(212, 181, 106, 0.28);
  box-shadow: inset 0 1px 0 rgba(255, 236, 190, 0.12);
}

.ud-premia-thumb--empty {
  color: #d4b56a;
}

.ud-premia-bar {
  height: 8px;
  border-radius: 999px;
  background: rgba(184, 144, 60, 0.18);
  overflow: hidden;
}

.ud-premia-bar-fill {
  background: linear-gradient(90deg, #8d6b2c, #d4b56a 55%, #f0dfad) !important;
  box-shadow: 0 0 10px rgba(212, 181, 106, 0.35);
}

.ud-premia-cta {
  background: linear-gradient(135deg, #1f1b17 0%, #3a2f22 100%) !important;
  color: #fff8ea !important;
  border: 1px solid rgba(212, 181, 106, 0.35) !important;
  font-weight: 700 !important;
  border-radius: 0.65rem !important;
  box-shadow: 0 8px 18px rgba(40, 28, 12, 0.18);
}

.ud-premia-cta:hover {
  color: #fff !important;
}

.ud-premia-cta-outline {
  background: transparent !important;
  color: #3a2f22 !important;
  border: 1px solid rgba(184, 144, 60, 0.55) !important;
  font-weight: 700 !important;
  border-radius: 0.65rem !important;
}

.ud-premia-cta-outline:hover {
  background: rgba(184, 144, 60, 0.1) !important;
}

.ud-ranking .list-group-item {
  border-color: #f1f5f9;
}

@media (max-width: 575.98px) {
  .ud-top-actions .btn,
  .ud-top-actions a.btn {
    flex: 1 1 calc(50% - 0.5rem);
  }

  .ud-date {
    width: 100%;
  }
}
</style>
